/**
 * Platform-neutral install support (iPhone, Android, desktop). Chromium browsers (Chrome/Edge/Samsung on
 * Android and desktop) fire `beforeinstallprompt`, which we capture and replay from a tap. iOS has no
 * prompt: any iOS browser adds web apps via Share → Add to Home Screen. Others: the browser menu.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

export function captureInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // keep it for our own "Install Repwise" button
    deferred = e as BeforeInstallPromptEvent;
    listeners.forEach((l) => l());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    listeners.forEach((l) => l());
  });
}

export function onInstallChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export const isIOS = () => typeof navigator !== 'undefined' && (/iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
export const isAndroid = () => typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export type InstallMethod = 'installed' | 'prompt' | 'share-sheet' | 'browser-menu';

export function installMethod(): InstallMethod {
  if (isInstalled()) return 'installed';
  if (deferred) return 'prompt';
  if (isIOS()) return 'share-sheet';
  return 'browser-menu';
}

/** Call from a tap. Resolves true if the user installed. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  await deferred.prompt();
  const { outcome } = await deferred.userChoice;
  deferred = null;
  listeners.forEach((l) => l());
  return outcome === 'accepted';
}

/** Copy for each method, device-neutral. */
export const INSTALL_STEPS: Record<Exclude<InstallMethod, 'installed' | 'prompt'>, string> = {
  'share-sheet': 'Tap Share, then Add to Home Screen. Open Repwise from your Home Screen afterwards.',
  'browser-menu': 'Open your browser menu and choose Install app or Add to Home screen.',
};
